import { AnalyticsCards } from "Common/Components/AnalyticsCards/AnalyticsCards";
import ActionButton from "Common/Components/Buttons/ActionButton";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import DynamicTabs from "components/DynamicTabs";
import LoadingSpinner from "components/LoadingSpinner";
import { ENQ } from "Constants/StatusConstant";
import { app_modules } from "helpers/apps_modules_list";
import usePermission from "hooks/usePermission";
import { CheckCircle, FileText, FolderOpen, PhoneCall } from "lucide-react";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { createSelector } from "reselect";
import {
  exportEnquiryCSV,
  fetchProductEnquiries,
  fetchServiceEnquiries,
} from "slices/enquiry/thunk";

import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { useResetTableOnTabChange } from "CustomHooks/useResetTableOnTabChange";
import { Add } from "@mui/icons-material";
import { MecordColors } from "Constants/Color";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import { defaultInnerStyle } from "Common/Components/PageContainer/PageContainer";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import { hideTagsSearchListingSection } from "../../utils/tagsSearchEmpty";

interface MasterListPageProps {
  activeType: "product" | "service";
  activeTab: string | undefined;
  title: string;
  addButtonLabel: string;
  addRoute: string;
  columns: any[];
  statusToggleUrl: string;
  reloadFlag?: boolean;
  /** Tags-search: keep user on /tags-search/:tags while switching Enquiry Log / Draft */
  onTagsSearchTabNavigate?: (tabValue: string) => void;
  noWrapper?: boolean;
  forceShowOnTagsSearch?: boolean;
}

const MasterListPage: React.FC<MasterListPageProps> = ({
  title,
  addButtonLabel,
  addRoute,
  columns,
  statusToggleUrl,
  activeTab,
  activeType,
  reloadFlag,
  onTagsSearchTabNavigate,
  noWrapper = false,
  forceShowOnTagsSearch = false,
}) => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();
  const prevTabRef = useRef<string | undefined>(undefined);

  /** Product vs service must not share the same key — both mount on tags-search and would fight over `last-tab` in sessionStorage and cause reset/fetch loops. */
  const preservedTableKey = React.useMemo(
    () => `${TableKeys.ENQUIRY.PRODUCT}-${activeType}`,
    [activeType],
  );

  const tableDefaults = React.useMemo(
    () => ({
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    }),
    [],
  );

  const tableState = usePreservedTableState({
    key: preservedTableKey,
    tabString: activeTab,
    defaults: tableDefaults,
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  // 🔍 If opened from /tags-search/:tags, use tag from URL for API and search box
  const { tags: tagsParam } = useParams<{ tags?: string }>();
  const tagFromUrl = React.useMemo(() => {
    if (!tagsParam) return "";
    try {
      return decodeURIComponent(tagsParam).trim();
    } catch {
      return tagsParam.trim();
    }
  }, [tagsParam]);
  // Effective search: when on tags-search route, always use URL tag so API gets it
  const effectiveSearch = tagFromUrl || search;

  // Sync URL tag to search box once when tagFromUrl is set (omit setters from deps to avoid infinite loop: hook returns new refs each render)
  React.useEffect(() => {
    if (!tagFromUrl) return;
    setSearchInput(tagFromUrl);
    setSearch(tagFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagFromUrl]);

  useResetTableOnTabChange(preservedTableKey, activeTab, resetState);

  // ✅ Select enquiry state
  const selectEnquiries = createSelector(
    (state: any) => state?.Enquiry,
    (enquiry) => ({
      list:
        activeType === "product" ? enquiry?.productList : enquiry?.serviceList,
      rowCount: enquiry.rowCount,
      loading: enquiry.loading,
      error: enquiry.error,
      todaysFollowUpCount: enquiry.todaysFollowUpCount,
    }),
  );

  // Static Card Data
  const statuses = [
    {
      label: "Total Enquiries",
      icon: <FileText size={16} />,
      bgColor: "bg-blue-50",
      textColor: "text-blue-600",
      count: 120,
    },
    {
      label: "Active Enquiries",
      icon: <FolderOpen size={16} />,
      bgColor: "bg-red-50",
      textColor: "text-red-600",
      count: 35,
    },
    {
      label: "Converted",
      icon: <CheckCircle size={16} />,
      bgColor: "bg-yellow-50",
      textColor: "text-yellow-600",
      count: 50,
    },
    {
      label: "Pending Follow-ups",
      icon: <PhoneCall size={16} />,
      bgColor: "bg-green-50",
      textColor: "text-green-600",
      count: 82,
    },
  ];

  const { loading, error, todaysFollowUpCount } = useSelector(selectEnquiries);

  // Get permissions based on activeType (product or service)
  const { canView, canCreate, canUpdate, canDelete } = usePermission(
    app_modules?.enquiry?.app_name,
    activeType === "product"
      ? app_modules?.enquiry?.modules?.product
      : app_modules?.enquiry?.modules?.service,
  );

  // ✅ Local state
  const [data, setData] = useState<any[]>([]);
  /** Per-instance totals (Redux `rowCount` is shared between product/service — breaks tag-search hide) */
  const [localRowCount, setLocalRowCount] = useState(0);
  const [listFetching, setListFetching] = useState(false);
  const [isStatusUpdateLoading, setIsStatusUpdateLoading] =
    useState<boolean>(false);
  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  // const [columnFilters, setColumnFilters] = useState<any>();
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [searchInput, setSearchInput] = useState("");
  // const [search, setSearch] = useState("");
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  const [tableInstance, setTableInstance] = useState<any>();
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  // ✅ Fetch list (use effectiveSearch so tag from URL is always sent when on /tags-search)
  const fetchEnquiryData = async () => {
    setListFetching(true);
    try {
      if (activeType === "product") {
        const resData = await dispatch(
          fetchProductEnquiries({
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? sortDirection : "",
            search: effectiveSearch,
            ...(tagFromUrl ? { type: "tag" } : {}),
            filters: filterValue,
            stage: activeTab || ENQ.ENQUIRY_LOG,
          }),
        ).unwrap();

        setData(resData.data);
        setLocalRowCount(resData.rowCount ?? 0);
      } else {
        const resData = await dispatch(
          fetchServiceEnquiries({
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? sortDirection : "",
            search: effectiveSearch,
            ...(tagFromUrl ? { type: "tag" } : {}),
            filters: filterValue,
            stage: activeTab,
          }),
        ).unwrap();

        setData(resData.data);
        setLocalRowCount(resData.rowCount ?? 0);
      }
    } catch (error: any) {
      console.error("Enquiry fetch failed:", error);

      // optional UX
      // toast.error(error?.message || "Failed to fetch enquiries");
      setData([]); // prevent UI crash
      setLocalRowCount(0);
    } finally {
      setListFetching(false);
    }
  };

  useEffect(() => {
    fetchEnquiryData();
  }, [
    activeType,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    effectiveSearch,
    filterValue,
    activeTab,
    dispatch,
    reloadFlag,
  ]);

  // useEffect(() => {
  //   if (!list || !Array.isArray(list)) return;

  //   const formatted = list.map((row: any) => ({
  //     id: row.enquiry_details?.id,
  //     enquiry_number: row.enquiry_details?.enquiry_number,
  //     enquiry_date: row.enquiry_details?.enquiry_date,
  //     company_name: row.enquiry_details?.company_name,
  //     source_of_enquiry: row.enquiry_details?.source_of_enquiry,
  //     agent: row.enquiry_details?.agent?.name || "N/A",
  //     reference: row.enquiry_details?.reference || "N/A",
  //     items: row.item_details?.length || 0,
  //     status: row.enquiry_details?.status || "N/A",
  //     emd: row.enquiry_details?.emd || "-",
  //     assigned_to: row.assigned_member_details?.map((m: any) => ({
  //       id: m.id,
  //       user_name: m.user_name || m.user,
  //     })),
  //     notes: row.enquiry_details?.notes || "",
  //     created_by_user: row.item_details?.[0]?.created_by_user || "N/A",
  //     created_at: row.item_details?.[0]?.created_at || null,
  //     updated_by_user: row.item_details?.[0]?.updated_by_user || "N/A",
  //     updated_at: row.item_details?.[0]?.updated_at || null,
  //   }));

  //   setData(formatted);
  // }, [list]);

  // ✅ Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  const handleExport = async (): Promise<{ blob: Blob; filename: string }> => {
    return await exportEnquiryCSV({
      search,
      filters: filterValue,
      type: activeType,
      stage: activeTab,
    });
  };

  function handleChange(e: any): void {
    setSearchInput(e.target.value);
  }


  const sectionContent = (
    <>



        <Row className="align-items-center">
          {/* LEFT */}
          <Col>
            <h6 className="card-title mb-0">{title}</h6>
          </Col>

        </Row>
        <Row className="align-items-center justify-content-between px-0 g-3 flex-wrap mb-3">


 
</Row>
        {/* ✅ Tabs */}
        <div className="d-flex align-items-center justify-content-between mt-1 pb-1">
          <DynamicTabs
            activeTab={activeTab}
            activeType={activeType}
            basePath="enquiry"
            onTabChange={onTagsSearchTabNavigate}
            tabs={[
              { value: "enquiry_log", label: "Enquiry Log" },
              // { value: "junk", label: "Junk" },
              { value: "draft", label: "Draft" },
            ]}
          />
        </div>


        {/* <Card className="mt-3">
          <Card.Body className="mt-0"> */}
            <div className="mt-3">
              <LoaderErrorWrapper
                loading={listFetching || loading || isStatusUpdateLoading}
                error={error}
              >
                <MasterTableContainer
                  columns={columns}
                  data={data || []}
                  totalRecords={localRowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  isBordered={false}
                  isPagination={true}
                  isGlobalFilter={false}
                  SearchPlaceholder={`Search ${title.toLowerCase()}...`}
                  manualSorting={true}
                  colValue={setFilterValue}
                  onTableReady={setTableInstance}
                />
              </LoaderErrorWrapper>
            </div>
          {/* </Card.Body>
        </Card> */}


    </>
  );

  if (
    hideTagsSearchListingSection(
      noWrapper,
      listFetching || loading || isStatusUpdateLoading,
      localRowCount,
      data?.length ?? 0,
      error,
    ) &&
    !forceShowOnTagsSearch
  ) {
    return null;
  }

  if (noWrapper) {
    return <div className="mb-4">{sectionContent}</div>;
  }

  return (
    <div className="">
      <div className="px-4" style={defaultInnerStyle}>
        {sectionContent}
      </div>
    </div>
  );
};

export default MasterListPage;
